import { FlashList } from '@shopify/flash-list';
import { parseISO, format, getYear, subMonths, isBefore } from 'date-fns';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState, } from 'react';
import Animated from 'react-native-reanimated';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { noop } from 'lodash';
import Button, { HEIGHT } from 'design-system/Button';
import FlatListItemCard from 'design-system/FlatListItemCard';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import ListEmptyView from 'design-system/ListEmptyView';
import StatusBar from 'design-system/StatusBar';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { AppView } from 'design-system/AppView';
import StackHeader from 'design-system/hoc/StackHeader';
import InfoCard from 'design-system/InfoCard';
import Text from 'design-system/Text';
import createStyleSheets from 'utils/createStyleSheets';
import Links, { knownLinks } from 'utils/links';
import RowTransaction from 'features/transactions/components/RowTransaction';
import useAdvancedEditTransactions from 'features/transactions/hooks/useAdvancedEditTransactions';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useColorOfCategory } from 'hooks/useCategory';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { getTransactionsFlat, } from 'utils/getTransactions';
import SwitchingButton from 'features/subscriptions/components/row/SwitchingButton';
import { useAppSelector } from 'store/hooks';
import SectionHeader from 'features/invest/components/SectionHeader';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import TransactionsHeader from 'features/transactions/components/TransactionsHeader';
import { useMarginBottom } from 'hooks/useMarginBottom';
import Modal from 'utils/packages/Modal';
import useNavigateTransactionItem from 'features/transactions/hooks/useNavigateTransactionItem';
import { CardView, Indicator, LineSeparator, Spacer } from 'design-system';
import BudgetingGraphLabel from 'features/budgeting/components/BudgetingGraphLabel';
import OpenInvestAccountCTA from 'features/pots/components/OpenInvestAccountCTA';
import Amplitude from '../../../utils/amplitude';
import { selectMonthlyCategoryForPeriod, selectMonthlyCategoryOrMerchantFromId, selectNeedsAnalyticsIntro, } from '../selectors';
import useSyncTotals from '../hooks/useSyncTotals';
import CategoryTopContainer from '../components/category/CategoryTopContainer';
import useSyncMonthlyExpenses from '../hooks/useSyncMonthlyExpenses';
import { isWeb } from '../../../constants';
const getItemType = (item) => item.type;
const keyExtractor = (item) => item.type === 'header' ? item.title : item.data.id.toString();
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const handleLearnMore = () => {
    Links.openLink(knownLinks.unknownMerchants);
};
const handleLearnMoreExcluded = () => {
    Links.openLink(knownLinks.excludedTransactions);
};
const CategoryDetailsScreen = withAnimatedNavigationHeader(({ navigation, route, scrollY, scrollHandler }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const navigateToTransactionItem = useNavigateTransactionItem();
    const { selected, isEditing, selectedObj, onEdit, onSelected, openEditModal, } = useAdvancedEditTransactions(navigation);
    const guessedHomeCountry = useAppSelector((store) => store.user.user.guessedHomeCountry);
    const data = useMemo(() => {
        if (route.params === undefined)
            return null;
        const { from, to, merchantId, categoryId, step, accountIds, runOnMount } = route.params;
        if (!to || !from)
            return null;
        let itemId;
        if (merchantId) {
            itemId = { isMerchant: true, merchantId, categoryId: undefined };
        }
        else if (categoryId) {
            itemId = { isMerchant: false, categoryId, merchantId: undefined };
        }
        else {
            return null;
        }
        return {
            ...itemId,
            step,
            monthEndDate: to,
            monthStartDate: from,
            fromCurrentMonth: from,
            accountIds,
            runOnMount,
        };
    }, [route.params]);
    const periodCategory = useAppSelector((store) => selectMonthlyCategoryForPeriod(store, data?.categoryId, data?.monthStartDate, data?.monthEndDate));
    const positionCategoryOrMerchant = useAppSelector((store) => selectMonthlyCategoryOrMerchantFromId(store, data?.categoryId, data?.merchantId));
    const categoryOrMerchant = useMemo(() => periodCategory
        ? { isMerchant: false, monthly: periodCategory }
        : positionCategoryOrMerchant, [periodCategory, positionCategoryOrMerchant]);
    const needsAnalyticsIntro = useAppSelector(selectNeedsAnalyticsIntro);
    useEffect(() => {
        if (needsAnalyticsIntro && !isWeb) {
            navigation.popTo('AnalyticsIntro');
            return;
        }
        if (!categoryOrMerchant && !data?.runOnMount) {
            navigation.popTo('Analytics');
        }
    }, [categoryOrMerchant, data?.runOnMount, needsAnalyticsIntro]);
    useLayoutEffect(() => {
        if (categoryOrMerchant) {
            const title = categoryOrMerchant.monthly.displayName;
            navigation.setOptions({
                headerRight: () => (<TransactionsHeader isEditing={isEditing} onPressEdit={onEdit}/>),
                headerTitle: () => <StackHeader title={title}/>,
                title,
            });
        }
    }, [categoryOrMerchant, isEditing, onEdit]);
    const [{ selectedPeriodStartDate }, setSelectedPeriod] = useState({
        selectedPeriodStartDate: data?.monthStartDate,
        selectedPeriodEndDate: data?.monthEndDate,
    });
    const [categoryExpenses, isFetchingCategoryExpenses] = useSyncMonthlyExpenses(data ?? undefined);
    useSyncTotals(data ?? undefined);
    useEffect(() => {
        let timeout;
        const categoryId = data?.categoryId;
        if (categoryId === 'internal') {
            AsyncStorage.getItem('FIRST_TIME_EXCLUDED')
                .then((value) => {
                if (value)
                    return;
                timeout = setTimeout(() => {
                    Modal.showConfirmation({
                        title: 'Excluded',
                        subtitle: 'This category is for transactions you want to hide from your analytics and budgeting, such as credit card repayments',
                        confirmButtonTitle: 'Good to know!',
                        brandReversedConfirm: true,
                    });
                });
                AsyncStorage.setItem('FIRST_TIME_EXCLUDED', 'true');
            })
                .catch(noop);
        }
        else if (categoryId === 'transfer') {
            AsyncStorage.getItem('FIRST_TIME_TRANSFER')
                .then((value) => {
                if (value)
                    return;
                timeout = setTimeout(() => {
                    Modal.showConfirmation({
                        title: 'Transfer',
                        subtitle: 'This category is for money moved between your own accounts. These transactions are excluded from your analytics and budgeting.',
                        confirmButtonTitle: 'Good to know!',
                        brandReversedConfirm: true,
                    });
                });
                AsyncStorage.setItem('FIRST_TIME_TRANSFER', 'true');
            })
                .catch(noop);
        }
        return () => clearTimeout(timeout);
    }, [data?.categoryId, route.params, styles.modal]);
    const onCompare = useCallback((routeWithParams) => {
        navigation.navigateDeprecated(routeWithParams);
        Amplitude.logEvent('Category.openCompare', {
            type: routeWithParams.name,
        });
    }, []);
    const { isUnlockedForSpace: isSearchUnlockedForSpace, onPressUpgrade } = useShouldShowUpgrade({
        benefitId: 'search',
    });
    const twoMonthsAgo = useMemo(() => subMonths(new Date(), 2), []);
    const isLocked = useMemo(() => {
        if (isSearchUnlockedForSpace) {
            return false;
        }
        return isBefore(selectedPeriodStartDate
            ? parseISO(selectedPeriodStartDate)
            : new Date(), twoMonthsAgo);
    }, [isSearchUnlockedForSpace, selectedPeriodStartDate, twoMonthsAgo]);
    const openItem = useCallback((data) => {
        if (isLocked) {
            onPressUpgrade();
            return;
        }
        const { isPending } = data;
        if (isEditing) {
            if (isPending)
                return;
            onSelected(data);
            return;
        }
        navigateToTransactionItem(data);
        Amplitude.logEvent('Category.OpenTransaction');
    }, [
        isEditing,
        isLocked,
        navigateToTransactionItem,
        onPressUpgrade,
        onSelected,
    ]);
    const renderRow = useCallback(({ item }) => {
        if (item.type === 'header') {
            let title = null;
            if (item.title !== 'Pending transactions') {
                const date = parseISO(item.title);
                const currentYear = getYear(new Date());
                if (currentYear === getYear(date)) {
                    title = format(date, 'EEEE, d MMMM');
                }
                else {
                    title = format(date, 'EEEE, d MMMM yyyy');
                }
            }
            else {
                title = 'Pending transactions';
            }
            return <SectionHeader title={title}/>;
        }
        return (<FlatListItemCard index={item.sectionIndex} style={styles.card} itemsLength={item.sectionLength}>
            <RowTransaction item={item.data} onPress={openItem} showUpgradeButton backgroundDark switchingButon={<SwitchingButton data={
                // Add merchandInfo if coming from SearchTransactions
                !item.data.subscription && item && item.data.merchant
                    ? { merchantInfo: item.data.merchant }
                    : item.data.subscription} onOpenCompare={onCompare} guessedHomeCountry={guessedHomeCountry}/>} blurValue={isLocked} isEditingTransactions={isEditing} selected={!!selectedObj[item.data.id]}/>
          </FlatListItemCard>);
    }, [
        guessedHomeCountry,
        isEditing,
        isLocked,
        onCompare,
        openItem,
        selectedObj,
        styles.card,
    ]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const transactionSections = useMemo(() => getTransactionsFlat(categoryExpenses), [categoryExpenses]);
    const colorOfCategory = useColorOfCategory();
    const color = categoryOrMerchant && !categoryOrMerchant.isMerchant
        ? colorOfCategory(categoryOrMerchant.monthly)
        : colors.categories.custom;
    const transferIncomeAndOutcome = useMemo(() => {
        if (categoryOrMerchant?.monthly.id !== 'transfer')
            return null;
        let income = 0;
        let outcome = 0;
        for (let i = 0; i < categoryExpenses.length; i++) {
            if (categoryExpenses[i].amount > 0) {
                income += categoryExpenses[i].amount;
            }
            else {
                outcome += categoryExpenses[i].amount;
            }
        }
        return { income, outcome };
    }, [categoryOrMerchant?.monthly?.id, categoryExpenses]);
    const renderListHeader = useMemo(() => {
        if (data && categoryOrMerchant) {
            if (categoryOrMerchant.isMerchant &&
                categoryOrMerchant.monthly.id === -1) {
                return (<InfoCard onPress={handleLearnMore} style={styles.infoCard}>
              <Text TextThin-14 Secondary>
                These transactions have not been matched with a merchant.{' '}
                <Text Text-14 Primary>
                  Learn more
                </Text>
              </Text>
            </InfoCard>);
            }
            if (!categoryOrMerchant.isMerchant &&
                categoryOrMerchant.monthly.id === 'internal') {
                const { totalWithExcluded, currency } = categoryOrMerchant.monthly;
                return (<>
              <InfoCard onPress={handleLearnMoreExcluded} style={styles.infoCard}>
                <Text TextThin-14 Secondary>
                  Excluded transactions don’t count in your analytics and
                  budgeting.{'\n'}
                  <Text Text-14 Primary>
                    Learn more
                  </Text>
                </Text>
              </InfoCard>
              <CardView style={styles.valueContainer} noMarginBottom>
                <BudgetingGraphLabel hasIndicator={false} title={totalWithExcluded > 0 ? 'Income' : 'Spending'} amount={{ amount: totalWithExcluded, currency }} amountProps={{
                        color: totalWithExcluded > 0
                            ? colors.text.income
                            : colors.text.primary,
                        showSign: true,
                    }}/>
              </CardView>
            </>);
            }
            if (!categoryOrMerchant.isMerchant && transferIncomeAndOutcome) {
                const { totalWithExcluded, currency } = categoryOrMerchant.monthly;
                const { income, outcome } = transferIncomeAndOutcome;
                return (<TransferCategoryHeader income={income} outcome={outcome} total={totalWithExcluded} currency={currency}/>);
            }
            return (<CategoryTopContainer scrollY={scrollY} step={data.step} categoryId={data.categoryId} merchantId={data.merchantId} defaultAmount={'totalWithExcluded' in categoryOrMerchant.monthly
                    ? categoryOrMerchant.monthly.totalWithExcluded
                    : categoryOrMerchant.monthly.total} defaultTo={data.monthEndDate} defaultFrom={data.monthStartDate} onSelectedPeriod={setSelectedPeriod} initialPositionDate={data.fromCurrentMonth} categoryColor={color} isSubcategory={!categoryOrMerchant.isMerchant &&
                    categoryOrMerchant.monthly.parentCategoryId != null}/>);
        }
        return null;
    }, [
        categoryOrMerchant,
        color,
        colors.text.income,
        colors.text.primary,
        data,
        scrollY,
        styles.infoCard,
        styles.valueContainer,
        transferIncomeAndOutcome,
    ]);
    const renderListEmpty = useMemo(() => {
        if (isFetchingCategoryExpenses) {
            return (<>
            <Spacer h={16}/>
            <Indicator dark/>
          </>);
        }
        return (<ListEmptyView happy onCard compact style={styles.empty} title="There are no transactions"/>);
    }, [isFetchingCategoryExpenses, styles.empty]);
    const isEditButtonVisible = isEditing && selected.length !== 0;
    const contentContainerStyle = useMarginBottom('paddingBottom', isEditButtonVisible ? HEIGHT + rem(16) : 0, paddingHorizontalStyle);
    const extraData = useMemo(() => [colors.cards.onDark, isEditing, isLocked, selectedObj], [colors.cards.onDark, isEditing, isLocked, selectedObj]);
    const renderListFooter = useMemo(() => {
        if (route.params?.categoryId === 'investments') {
            return <OpenInvestAccountCTA withTopSpacing/>;
        }
        return null;
    }, [route.params?.categoryId]);
    const baseProps = {
        extraData,
        keyExtractor,
        renderItem: renderRow,
        contentContainerStyle,
        scrollEventThrottle: 8,
        onScroll: scrollHandler,
        data: transactionSections,
        onEndReachedThreshold: 0.8,
        ListEmptyComponent: renderListEmpty,
        ListHeaderComponent: renderListHeader,
        showsVerticalScrollIndicator: true,
        scrollIndicatorInsets: SCROLL_INDICATOR_INSETS,
        ListFooterComponent: renderListFooter,
    };
    const list = isWeb ? (<Animated.FlatList {...baseProps}/>) : (<AnimatedFlashList {...baseProps} getItemType={getItemType}/>);
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        {list}
        {isEditButtonVisible ? (<Button brand floating title={`Edit (${selected.length}) transactions`} onPress={openEditModal}/>) : null}
      </AppView>);
}, ({ colors }) => ({
    hasCustomTitle: true,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const TransferCategoryHeader = ({ income, outcome, total, currency, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    return (<>
      <InfoCard style={styles.infoCard}>
        <Text TextThin-14 Secondary>
          You can use this category for moving money between your own accounts.
          Ideally, your net balance here should be{' '}
          <Text NumbersThin-14 Secondary>
            £0
          </Text>
          .
        </Text>
      </InfoCard>
      <CardView style={styles.valueContainer} noMarginBottom>
        <BudgetingGraphLabel hasIndicator={false} title="Outgoings" amount={{
            amount: outcome,
            currency,
        }} amountProps={{ color: colors.text.primary }}/>
        <BudgetingGraphLabel hasIndicator={false} title="Incomings" amount={{
            amount: income,
            currency,
        }} amountProps={{
            color: income > 0 ? colors.text.income : colors.text.primary,
            showSign: true,
        }}/>
        <LineSeparator style={styles.lineSeparator} noMargin/>
        <BudgetingGraphLabel hasIndicator={false} title="Net balance" amount={{
            amount: total,
            currency,
        }} amountProps={{
            color: total > 0 ? colors.text.income : colors.text.primary,
            showSign: true,
        }}/>
      </CardView>
    </>);
};
const styleSet = createStyleSheets((colors) => ({
    empty: {
        marginTop: rem(16),
    },
    modal: {
        paddingHorizontal: 0,
        overflow: 'hidden',
    },
    card: {
        backgroundColor: colors.cards.onDark,
    },
    infoCard: {
        marginTop: rem(16),
    },
    valueContainer: {
        paddingHorizontal: rem(16),
        paddingVertical: rem(12),
    },
    lineSeparator: {
        marginVertical: rem(12),
    },
}));
export default CategoryDetailsScreen;
